import type { LiveHint } from "@beacon/core/chat"
import { Refused } from "@/administration/refused.ts"

/*
 * Chat's live socket, as a tab holds it: one per signed-in tab, opened with a ticket the web API
 * issues its session, kept open by pings, and reopened, with a new ticket, whenever it closes or
 * stops answering. What it hears are hints (`LiveHint`): ids that say something changed, never
 * what. A tab catches up over HTTP from its cursor on each, and on every (re)opening, so a hint
 * lost while the socket was down costs nothing but the wait.
 */

/**
 * How the socket stands: `connecting` the first time, `live` while open, `reconnecting` after it
 * closed or never opened, until it opens again, and `unavailable` when there is none to open (a
 * stage without one, or a session that ended), when only catching up on a timer keeps a tab current.
 */
export type LiveStatus = "connecting" | "live" | "reconnecting" | "unavailable"

export const liveTimings = {
  /** How often an open socket pings: well within API Gateway's ten idle minutes. */
  pingEvery: 4 * 60_000,
  /** How long a ping may go unanswered before the socket is taken for dead. */
  answerWithin: 15_000,
  /** When an open socket is renewed: before API Gateway ends every one at two hours. */
  renewAfter: 110 * 60_000,
}

/**
 * How long to wait before reconnecting after `attempt` failures in a row: doubling from a second
 * to at most 30, spread over its upper half by `random` so tabs dropped together come back apart.
 */
export function reconnectDelay(attempt: number, random: () => number = Math.random) {
  const longest = Math.min(1_000 * 2 ** attempt, 30_000)
  return Math.round(longest / 2 + (random() * longest) / 2)
}

/** What the socket needs of a WebSocket: a browser's, or a test's. */
export type LiveSocketLike = {
  addEventListener(
    type: "open" | "message" | "close" | "error",
    listener: (event: { data?: unknown }) => void,
  ): void
  send(data: string): void
  close(): void
}

/** A hint a client acts on: a pong only keeps the socket current. */
export type ChatHint = Exclude<LiveHint, { kind: "pong" }>

export type LiveSocketDeps = {
  /** A ticket for this session and where to use it (`POST /chat/live`); throws `Refused`. */
  ticket(): Promise<{ url: string; ticket: string }>
  open(url: string): LiveSocketLike
  /** Each hint, as it arrives. */
  onHint(hint: ChatHint): void
  /** The socket opened, the first time or again: whatever was missed meanwhile is caught up now. */
  onOpen(): void
  onStatus(status: LiveStatus): void
  random?: () => number
}

const isHint = (said: unknown): said is ChatHint => {
  const hint = said as {
    kind?: unknown
    conversationId?: unknown
    seq?: unknown
    change?: unknown
    memberId?: unknown
    typing?: unknown
    thread?: unknown
  } | null
  if (!hint || typeof hint !== "object") return false
  if (hint.kind === "revoked") return typeof hint.conversationId === "string"
  if (hint.kind === "mentions" || hint.kind === "attention" || hint.kind === "inbox") return true
  if (hint.kind === "read")
    return (
      typeof hint.conversationId === "string" &&
      (hint.thread === undefined || typeof hint.thread === "string")
    )
  if (hint.kind === "presence") return typeof hint.memberId === "string"
  if (hint.kind === "typing")
    return (
      typeof hint.conversationId === "string" &&
      typeof hint.memberId === "string" &&
      typeof hint.typing === "boolean"
    )
  return (
    hint.kind === "changed" &&
    typeof hint.conversationId === "string" &&
    typeof hint.seq === "number" &&
    typeof hint.change === "string"
  )
}

/**
 * A live socket, idle until `start`. `watch` asks to be told of a conversation the Member hasn't
 * joined (those they have are told anyway), and answers how to stop; `retry` reconnects at once if
 * it is waiting to; `stop` closes it for good. `presence` says whether the Member is idle, now and
 * on every (re)opening, which also makes the socket hear everyone's presence change; until it is
 * first called, the socket says nothing of it. `typing` says the Member is typing in a
 * conversation, or stopped, when the socket is open (and nothing otherwise: typing is a moment's).
 */
export function createLiveSocket({
  ticket,
  open,
  onHint,
  onOpen,
  onStatus,
  random = Math.random,
}: LiveSocketDeps) {
  const watching = new Map<string, number>()
  let idle: boolean | undefined
  let status: LiveStatus | undefined
  let socket: LiveSocketLike | undefined
  let isOpen = false
  let fetching = false
  let stopped = true
  let failures = 0
  let retryTimer: ReturnType<typeof setTimeout> | undefined
  let pingTimer: ReturnType<typeof setInterval> | undefined
  let answerTimer: ReturnType<typeof setTimeout> | undefined
  let renewTimer: ReturnType<typeof setTimeout> | undefined

  function set(next: LiveStatus) {
    if (status === next) return
    status = next
    onStatus(next)
  }

  function say(message: object) {
    if (socket && isOpen) socket.send(JSON.stringify(message))
  }

  function clearTimers() {
    clearTimeout(retryTimer)
    clearInterval(pingTimer)
    clearTimeout(answerTimer)
    clearTimeout(renewTimer)
    retryTimer = pingTimer = answerTimer = renewTimer = undefined
  }

  /** Lets the current socket go and opens another after the backoff. */
  function reconnect() {
    const was = socket
    socket = undefined
    isOpen = false
    clearTimers()
    was?.close()
    if (stopped) return
    set("reconnecting")
    retryTimer = setTimeout(() => void connect(), reconnectDelay(failures++, random))
  }

  async function connect() {
    if (fetching) return
    clearTimers()
    let issued: { url: string; ticket: string }
    fetching = true
    try {
      issued = await ticket()
    } catch (error) {
      if (stopped) return
      // A refusal is final (no live socket on this stage, or no session); the network isn't.
      if (error instanceof Refused && error.refusal !== "unknown") return set("unavailable")
      return reconnect()
    } finally {
      fetching = false
    }
    if (stopped) return
    const url = `${issued.url}?ticket=${encodeURIComponent(issued.ticket)}`
    const next = open(url)
    socket = next
    next.addEventListener("open", () => {
      if (socket !== next) return
      isOpen = true
      failures = 0
      set("live")
      if (idle !== undefined) say({ action: "presence", idle })
      for (const conversationId of watching.keys()) say({ action: "watch", conversationId })
      pingTimer = setInterval(() => {
        say({ action: "ping" })
        clearTimeout(answerTimer)
        answerTimer = setTimeout(reconnect, liveTimings.answerWithin)
      }, liveTimings.pingEvery)
      renewTimer = setTimeout(reconnect, liveTimings.renewAfter)
      onOpen()
    })
    next.addEventListener("message", (event) => {
      if (socket !== next) return
      clearTimeout(answerTimer)
      let said: unknown
      try {
        said = JSON.parse(String(event.data))
      } catch {
        return
      }
      if (isHint(said)) onHint(said)
    })
    next.addEventListener("close", () => {
      if (socket === next) reconnect()
    })
  }

  return {
    start() {
      if (!stopped) return
      stopped = false
      set("connecting")
      void connect()
    },
    stop() {
      stopped = true
      const was = socket
      socket = undefined
      isOpen = false
      clearTimers()
      was?.close()
    },
    retry() {
      if (stopped || status !== "reconnecting" || socket) return
      failures = 0
      void connect()
    },
    watch(conversationId: string) {
      const count = watching.get(conversationId) ?? 0
      watching.set(conversationId, count + 1)
      if (count === 0) say({ action: "watch", conversationId })
      let done = false
      return () => {
        if (done) return
        done = true
        const left = (watching.get(conversationId) ?? 1) - 1
        if (left > 0) return void watching.set(conversationId, left)
        watching.delete(conversationId)
        say({ action: "unwatch", conversationId })
      }
    },
    presence(nowIdle: boolean) {
      if (idle === nowIdle) return
      idle = nowIdle
      say({ action: "presence", idle })
    },
    typing(conversationId: string, typing: boolean) {
      say({ action: "typing", conversationId, typing })
    },
  }
}

export type LiveSocket = ReturnType<typeof createLiveSocket>
